import Big from "big.js";
import { BillableItemDTO } from "./BilliableItemDTO";
import { ComboMPPDTO, defaultComboMPPDTO } from "./ComboMPPDTO";

export interface CompleteBudgetDetailDTO {
    budgetdetail: bigint | null
    description: string | null
    unitprice: number | null
    valueday: number | null
    valuemonth: number | null
    homehospitalization: boolean | null
    /** En la factura sanatorial puede tener decimales (medicación cobrada en proporción a la dosis). */
    unitsqty: number | null
    type: bigint | null
    itemid: bigint | null
    patientamount: number | null
    insuranceamount: number | null
    nomenclator: bigint | null
    hoursbymonth: number | null
    visitsbymonth: number | null
    comboMPPDTO: ComboMPPDTO | null
    packageqty: bigint | null
    bars: string | null
    unitsperpackage: bigint | null
    item: bigint | null
    billableitems?: BillableItemDTO[];
    healthentitypercentagecoverage: number | null;
    patientpercentagecoverage: number | null;
}

export interface BudgetDetailDTO {
    budgetdetail: number | null;
    description: string;
    unitprice: number;
    medicalamount: number;
    expensesamount: number;
    medicalpercentage: number;
    expensespercentage: number;
    valueday: number;
    valuemonth: number;
    /** En la factura sanatorial puede tener decimales (medicación cobrada en proporción a la dosis). */
    unitsqty: number;
    type: number;
    itemid: number | null;
    billableitems?: BillableItemDTO[];
    healthentitypercentagecoverage: number | null;
    patientpercentagecoverage: number | null;
    applicationdate?: string | null;
}

export interface ProfessionalActivityBudgetDetailDTO extends BudgetDetailDTO {
    nomenclator: number;
    hoursbymonth: number;
    visitsbymonth: number;
}

export interface DisposablesBudgetDetailDTO extends BudgetDetailDTO {
    item: number;

    earnings: number;
    pricesettingdate:Date | null;
}

export interface EquipmentBudgetDetailDTO extends BudgetDetailDTO {
    item: number;

    earnings: number;
}

export interface NutritionalSupportBudgetDetailDTO extends BudgetDetailDTO {
    comboMPPDTO: ComboMPPDTO;

    packageqty: number;
    earnings: number;
}

export interface MedicinesBudgetDetailDTO extends BudgetDetailDTO {
    comboMPPDTO: ComboMPPDTO;
    packageqty: number;
    earnings: number;
}

export interface HospitalizationDaysBudgetDetailDTO extends BudgetDetailDTO {
    nomenclator: number;
    hoursbymonth: number;
    visitsbymonth: number;
}

export interface ExtrasBudgetDetailDTO extends BudgetDetailDTO {

}

export type CoveragePercentagesDTO = {
    healthentitypercentagecoverage?: number | null;
    patientpercentagecoverage?: number | null;
};

export const DEFAULT_HEALTH_ENTITY_PERCENTAGE_COVERAGE = 0;
export const DEFAULT_PATIENT_PERCENTAGE_COVERAGE = 100;

export const withDefaultCoveragePercentages = <T extends CoveragePercentagesDTO>(item: T): T => {
    const healthEntityPercentage = item.healthentitypercentagecoverage;
    const patientPercentage = item.patientpercentagecoverage;

    if (
        (healthEntityPercentage == null && patientPercentage == null) ||
        (healthEntityPercentage === 0 && patientPercentage === 0) ||
        (healthEntityPercentage == null && patientPercentage === 0)
    ) {
        return {
            ...item,
            healthentitypercentagecoverage: DEFAULT_HEALTH_ENTITY_PERCENTAGE_COVERAGE,
            patientpercentagecoverage: DEFAULT_PATIENT_PERCENTAGE_COVERAGE
        };
    }

    if (healthEntityPercentage != null && (patientPercentage == null || healthEntityPercentage + patientPercentage !== 100)) {
        return {
            ...item,
            healthentitypercentagecoverage: healthEntityPercentage,
            patientpercentagecoverage: DEFAULT_PATIENT_PERCENTAGE_COVERAGE - healthEntityPercentage
        };
    }

    if (patientPercentage != null && healthEntityPercentage == null) {
        return {
            ...item,
            healthentitypercentagecoverage: DEFAULT_PATIENT_PERCENTAGE_COVERAGE - patientPercentage,
            patientpercentagecoverage: patientPercentage
        };
    }

    return item;
};

export const defaultGridBudgetDTO: BudgetDetailDTO = {
    budgetdetail: null,
    description: '',
    unitprice: 0,
    medicalamount: 0,
    expensesamount: 0,
    medicalpercentage: 0,
    expensespercentage: 0,
    valueday: 0,
    valuemonth: 0,
    unitsqty: 0,
    type: 0,
    itemid: 0,
    healthentitypercentagecoverage: 0,
    patientpercentagecoverage: 0
};

export const defaultProfessionalActivityBudgetDetailDTO: ProfessionalActivityBudgetDetailDTO = {
    ...defaultGridBudgetDTO,
    nomenclator: -1,
    hoursbymonth: 0,
    visitsbymonth: 0,
    unitsqty: 0,
};

export const defaultDisposablesBudgetDetailDTO: DisposablesBudgetDetailDTO = {
    ...defaultGridBudgetDTO,
    item: -1,
    unitsqty: 0,
    earnings:0,
    pricesettingdate:null
};

export const defaultEquipmentBudgetDetailDTO: EquipmentBudgetDetailDTO = {
    ...defaultGridBudgetDTO,
    item: -1,
    unitsqty: 0,
    earnings:0
};

export const defaultNutritionalSupportBudgetDetailDTO: NutritionalSupportBudgetDetailDTO = {
    ...defaultGridBudgetDTO,
    comboMPPDTO:defaultComboMPPDTO,
    unitsqty: 0,
    packageqty: 0,
    earnings:0
};

export const defaultMedicinesBudgetDetailDTO: MedicinesBudgetDetailDTO = {
    ...defaultGridBudgetDTO,
    comboMPPDTO:defaultComboMPPDTO,
    unitsqty: 0,
    packageqty: 0,
    earnings:0
};

export const defaultHospitalizationDaysBudgetDetailDTO: HospitalizationDaysBudgetDetailDTO = {
    ...defaultGridBudgetDTO,
    nomenclator: -1,
    hoursbymonth: 0,
    visitsbymonth: 0,
    unitsqty: 0,
};

export const defaultExtrasBudgetDetailDTO: ExtrasBudgetDetailDTO = {
    ...defaultGridBudgetDTO,
    unitsqty: 0,
};

export const calculateDistributionPercentages = (medicalAmount: number, expensesAmount: number): { medicalPercentage: number; expensesPercentage: number } => {
    const total = medicalAmount + expensesAmount;
    if (total === 0) return { medicalPercentage: 0, expensesPercentage: 0 };
    return {
        medicalPercentage: medicalAmount / total,
        expensesPercentage: expensesAmount / total
    };
};

export const distributeByPercentage = (unitprice: number, medicalPercentage: number, expensesPercentage: number): { medicalamount: number; expensesamount: number } => {
    if (medicalPercentage === 0 && expensesPercentage === 0) {
        return { medicalamount: unitprice, expensesamount: 0 };
    }
    const medicalamount = new Big(unitprice).times(medicalPercentage).round(2, Big.roundHalfUp);
    const expensesamount = new Big(unitprice).minus(medicalamount).round(2, Big.roundHalfUp);
    return { medicalamount: medicalamount.toNumber(), expensesamount: expensesamount.toNumber() };
};

export const getSubtotalValueDay = (items: BudgetDetailDTO[]): number => {
    return items.map((item) => item.valueday).reduce((prev, next) => prev + next, 0);
}

export const getSubtotalValueMonth = (items: BudgetDetailDTO[]): number => {
    return items.map((item) => item.valuemonth).reduce((prev, next) => prev + next, 0);
}
